/**
 * Página de Detalle de Club
 * Muestra información del club y sus unidades/patrullas
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  RoleBadge,
  SectionBadge,
} from "@/components/ui";
import {
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Users,
  Loader2,
  Calendar,
  Clock,
  Phone,
  Mail,
  Flag,
  Building2,
  User,
  Church as ChurchIcon,
  MapPin,
} from "lucide-react";
import {
  useClub,
  useUnitsByClub,
  useDeleteUnit,
  useChurch,
} from "@/hooks/useOrganization";
import { useMembersByClub } from "@/hooks/usePersons";
import UnitModal from "./components/UnitModal";
import ClubModal from "./components/ClubModal";
import FusionPanel from "./components/FusionPanel";
import JoinRequestsPage from "@/pages/members/JoinRequestsPage";
import SectionDirectorsPanel from "@/pages/settings/components/SectionDirectorsPanel";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/authStore";
import { Can } from "@/components/auth/Can";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import type { Unit } from "@/types";
import type { ClubType } from "@/types";
import { getActiveSections } from "@/lib/utils";

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
};

const statusLabels: Record<string, string> = {
  active: "Activa",
  inactive: "Inactiva",
};

export default function ClubDetailPage() {
  const { clubId } = useParams<{ clubId: string }>();
  const navigate = useNavigate();
  const { isUnionAdmin, isAssociationAdmin } = usePermissions();
  const { profile } = useAuthStore();
  // Gestión (solicitudes + directores de sección): admins de unión/asociación
  // o el director general del propio club. Las CFs re-validan server-side.
  const canManageClub =
    isUnionAdmin ||
    isAssociationAdmin ||
    profile?.directorOfClub?.id === clubId;

  const { data: club, isLoading: loadingClub } = useClub(clubId || "");
  const { data: units, isLoading: loadingUnits } = useUnitsByClub(clubId || "");
  const { data: members } = useMembersByClub(clubId || "");
  const { data: church } = useChurch(club?.churchId || "");
  const deleteUnitMutation = useDeleteUnit();

  const [showUnitModal, setShowUnitModal] = useState(false);
  const [showClubModal, setShowClubModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitToDelete, setUnitToDelete] = useState<Unit | null>(null);

  const handleEditUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setShowUnitModal(true);
  };

  const handleDeleteUnit = async () => {
    if (!unitToDelete || !clubId) return;

    try {
      await deleteUnitMutation.mutateAsync({
        id: unitToDelete.id,
        clubId,
      });
      setUnitToDelete(null);
    } catch (error) {
      console.error("Error deleting unit:", error);
    }
  };

  const handleCloseUnitModal = () => {
    setShowUnitModal(false);
    setSelectedUnit(null);
  };

  // Contar miembros por unidad
  const getMembersInUnit = (unitId: string) => {
    return (
      members?.filter((m) => m.unitId === unitId && m.status === "active")
        .length || 0
    );
  };

  // Miembros sin unidad asignada
  const membersWithoutUnit =
    members?.filter((m) => !m.unitId && m.status === "active").length || 0;

  if (loadingClub) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </div>
    );
  }

  if (!club) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Club no encontrado</p>
        <Button onClick={() => navigate("/organization")} className="mt-4">
          Volver a Organización
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: "Organización", to: "/organization" },
          { label: club.name },
        ]}
      />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3 sm:gap-4 min-w-0">
          <Button
            variant="ghost"
            onClick={() => navigate("/organization")}
            className="p-2 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Logo del club o icono por defecto */}
              {club.logoUrl ? (
                <img
                  src={club.logoUrl}
                  alt={`Logo de ${club.name}`}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl object-cover shadow-lg ring-2 ring-white flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-accent-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30 flex-shrink-0">
                  <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              )}
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words min-w-0">
                {club.name}
              </h1>
              {getActiveSections(club).map((s) => (
                <SectionBadge key={s} section={s as ClubType} size="sm" />
              ))}
              <Badge className={statusColors[club.status]}>
                {statusLabels[club.status]}
              </Badge>
            </div>
            <p className="text-gray-500 mt-1 text-sm sm:text-base">
              Gestiona la información del club y sus unidades
            </p>
          </div>
        </div>
        <Can permission="club.write" clubId={clubId}>
          <Button
            onClick={() => setShowClubModal(true)}
            className="w-full sm:w-auto flex-shrink-0"
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar Club
          </Button>
        </Can>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/25 group-hover:scale-110 transition-transform duration-300">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {members?.filter((m) => m.status === "active").length || 0}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Miembros activos
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:scale-110 transition-transform duration-300">
                <Flag className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {units?.filter((u) => u.status === "active").length || 0}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Unidades activas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900 capitalize">
                  {club.meetingDay || "-"}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Día de reunión
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25 group-hover:scale-110 transition-transform duration-300">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {club.meetingTime || "-"}
                </p>
                <p className="text-sm font-medium text-gray-500">
                  Hora de reunión
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Iglesia y Distrito */}
      {(church || club.churchName) && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Iglesia</h3>
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center gap-2">
                <ChurchIcon className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900">
                  {church?.name || club.churchName}
                </span>
                {church?.code && (
                  <span className="text-xs text-gray-400">· {church.code}</span>
                )}
              </div>
              {church?.districtName && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-700">
                    Distrito:{" "}
                    <span className="font-medium text-gray-900">
                      {church.districtName}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Info */}
      {(club.contactEmail || club.contactPhone) && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              Información de Contacto
            </h3>
            <div className="flex flex-wrap gap-6">
              {club.contactEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span>{club.contactEmail}</span>
                </div>
              )}
              {club.contactPhone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span>{club.contactPhone}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members Section */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Miembros del Club</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {members?.filter((m) => m.status === "active").length || 0}{" "}
              miembro(s) activo(s)
            </p>
          </div>
          <Button variant="secondary" onClick={() => navigate("/members")}>
            Ver todos
          </Button>
        </CardHeader>
        <CardContent>
          {!members || members.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">
                Este club no tiene miembros registrados
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {members
                .slice()
                .sort((a, b) => {
                  const order = {
                    director: 0,
                    subdirector: 1,
                    secretario: 2,
                    tesorero: 3,
                    instructor: 4,
                    consejero: 5,
                    member: 6,
                  } as Record<string, number>;
                  const ra = order[a.role] ?? 99;
                  const rb = order[b.role] ?? 99;
                  if (ra !== rb) return ra - rb;
                  return (
                    a.person?.fullName ||
                    a.personName ||
                    ""
                  ).localeCompare(b.person?.fullName || b.personName || "");
                })
                .map((m) => (
                  <button
                    key={m.id}
                    onClick={() => navigate(`/members/${m.personId}`)}
                    className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg transition-colors text-left"
                  >
                    {m.person?.photoUrl || m.personPhoto ? (
                      <img
                        src={m.person?.photoUrl || m.personPhoto}
                        alt={m.person?.fullName || m.personName}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white shadow">
                        <User className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {m.person?.fullName || m.personName || "Sin nombre"}
                      </p>
                      <div className="mt-0.5">
                        <RoleBadge
                          role={m.role}
                          section={m.section}
                          size="xs"
                        />
                      </div>
                    </div>
                    <Badge
                      className={
                        statusColors[m.status] || "bg-gray-100 text-gray-800"
                      }
                    >
                      {statusLabels[m.status] || m.status}
                    </Badge>
                  </button>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Units Section */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Unidades / Patrullas</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Organiza a los miembros en unidades para mejor gestión
            </p>
          </div>
          <Can permission="club.write" clubId={clubId}>
            <Button onClick={() => setShowUnitModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Nueva Unidad
            </Button>
          </Can>
        </CardHeader>
        <CardContent>
          {loadingUnits ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : units?.length === 0 ? (
            <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Flag className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 mb-4 font-medium">
                No hay unidades creadas para este club
              </p>
              <Can permission="club.write" clubId={clubId}>
                <Button onClick={() => setShowUnitModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Crear primera unidad
                </Button>
              </Can>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Warning for members without unit */}
              {membersWithoutUnit > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-4 flex items-start gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md shadow-amber-500/25 flex-shrink-0">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">
                      {membersWithoutUnit} miembro(s) sin unidad asignada
                    </p>
                    <p className="text-sm text-amber-600">
                      Asigna estos miembros a una unidad desde su ficha de
                      inscripción
                    </p>
                  </div>
                </div>
              )}

              {/* Units Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {units?.map((unit) => (
                  <div
                    key={unit.id}
                    className="border-2 border-gray-100 rounded-xl p-4 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: unit.color || "#3B82F6" }}
                        >
                          <Flag className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {unit.name}
                          </h3>
                          {unit.motto && (
                            <p className="text-xs text-gray-500 italic">
                              "{unit.motto}"
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge className={statusColors[unit.status]}>
                        {statusLabels[unit.status]}
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>{getMembersInUnit(unit.id)} miembros</span>
                      </div>

                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEditUnit(unit)}
                          className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => setUnitToDelete(unit)}
                          className="p-2 hover:bg-red-50 rounded-xl transition-colors duration-200"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* F11: panel de club fusión — admins completo; director de un club de
          ORIGEN solo su bloque (agrega a su gente, sin disolver) */}
      {club.isFusion && (isUnionAdmin || isAssociationAdmin) && (
        <FusionPanel club={club} />
      )}
      {club.isFusion &&
        !(isUnionAdmin || isAssociationAdmin) &&
        profile?.directorOfClub &&
        club.fusionOf?.includes(profile.directorOfClub.id) && (
          <FusionPanel club={club} originOnly={profile.directorOfClub.id} />
        )}

      {/* Gestión del club: solicitudes de unión + directores de sección */}
      {canManageClub && clubId && (
        <>
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Solicitudes de unión</CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Aprueba o rechaza las solicitudes pendientes de este club
              </p>
            </CardHeader>
            <CardContent>
              <JoinRequestsPage clubId={clubId} embedded />
            </CardContent>
          </Card>

          <SectionDirectorsPanel clubId={clubId} />
        </>
      )}

      {/* Unit Modal */}
      <UnitModal
        isOpen={showUnitModal}
        onClose={handleCloseUnitModal}
        unit={selectedUnit}
        clubId={clubId || ""}
      />

      {/* Club Modal */}
      {showClubModal && (
        <ClubModal
          isOpen={showClubModal}
          onClose={() => setShowClubModal(false)}
          club={club}
          churchId={club.churchId}
        />
      )}

      {/* Delete Confirmation Modal */}
      {unitToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmar eliminación
            </h3>
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar la unidad{" "}
              <strong>{unitToDelete.name}</strong>?
              {getMembersInUnit(unitToDelete.id) > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Esta unidad tiene {getMembersInUnit(unitToDelete.id)}{" "}
                  miembro(s) asignado(s).
                </span>
              )}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setUnitToDelete(null)}>
                Cancelar
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteUnit}
                disabled={deleteUnitMutation.isPending}
              >
                {deleteUnitMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Eliminando...
                  </>
                ) : (
                  "Eliminar"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
